import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import { setShowPotCollectionReload, setCriticalAnimationPlaying, setUserInterfaceDisabled } from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { SFX_SPRITE_MAP } from "../../../sfxSpriteMap";

// Tap anywhere → fullscreen + dismiss

// Device and orientation-specific config (same structure as GJP / Outro)
// buttonYOffset: TAP TO CONTINUE Y offset (fraction of symbolHeight, positive = down)
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.0, offset: 0.0, buttonYOffset: 1.25 },
  MOBILE_PORTRAIT: { scale: 1.0, offset: 0.0, buttonYOffset: 2.75 },
  MOBILE_LANDSCAPE: { scale: 0.8, offset: 0, buttonYOffset: 1.0 },
  TABLET_PORTRAIT: { scale: 1.4, offset: 0.0, buttonYOffset: 0 },
  TABLET_LANDSCAPE: { scale: 1.3, offset: 0.0, buttonYOffset: 0 },
  DESKTOP: { scale: 1.0, offset: 0.0, buttonYOffset: 0 },
};

/**
 * PotCollectionReloadController
 *
 * Manages the "Respins Left" reload/resume popup for the Pot Collection bonus game.
 * Shown when the user reloads mid-bonus (after at least one respin has been played).
 *
 * Text slots (from respins_left_popup spine):
 *   - txt_slot_value            → respins remaining number (GrekoNew counter font)
 *   - txt_slot_respins          → "RESPINS LEFT" (localized)
 *   - txt_slot_tap_to_continue  → "TAP TO CONTINUE"
 *
 * Flow: in → idle → user click → out → dispatch hide
 */
class PotCollectionReloadController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.reloadSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.hasHandledCloseClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.inIdleTimeout = null;

    // Click handlers (set by component via setButtonClickHandler)
    this._buttonClickHandler = null;

    // Respins data
    this._respinsLeft = 0;

    // Resolve callback for awaiting dismissal
    this._dismissResolve = null;

    // Value (attached to spine slot)
    this.valueContainer = null;
    this.valueText = null;

    // Respins label (attached to spine slot)
    this.respinsContainer = null;
    this.respinsText = null;

    // Button (attached to spine slot)
    this.buttonContainer = null;
    this.buttonText = null;

    // Internal caches
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING
  // ===================================================================

  setButtonClickHandler(handler) {
    this._buttonClickHandler = handler;
  }

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext;

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = isMobileSmall
          ? DEVICE_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS.MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS.TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS.DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.reloadSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.reloadSpine.scale.set(scale);

    const baseOffset = this.symbolHeight || 0;
    this.reloadSpine.x = 0;
    this.reloadSpine.y = -baseOffset * deviceOffsetMultiplier;
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation ||
      this._lastIsMobileSmall !== dimensionsContext.isMobileSmall
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (
      this.reloadSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
        this.applyButtonOffset();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION
  // ===================================================================

  initializeSpine() {
    try {
      this.reloadSpine = this.createSpine();

      if (!this.reloadSpine) return;
      if (!this.reloadSpine.skeleton || !this.reloadSpine.state) return;
      if (!this.reloadSpine.skeleton.data) return;

      this.container.addChild(this.reloadSpine);
      this.reloadSpine.alpha = 0.001;
      this.reloadSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.reloadSpine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      // Configure animation mixing for smooth transitions
      const stateData = this.reloadSpine.state.data;
      stateData.setMix("in", "idle", 0.15);
      stateData.setMix("idle", "out", 0.25);

      this.warmUpSpine();
      this.initializeValue();
      this.initializeRespinsLabel();
      this.initializeButton();

      // Force-upload atlas textures to GPU
      SpineAtlasUploader.upload("respins_left_popup");
    } catch {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "respins_left_popup-skeleton";
      const atlasAlias = "respins_left_popup-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine?.skeleton?.data?.animations) return null;

      return spine;
    } catch {
      return null;
    }
  }

  warmUpSpine() {
    if (!this.reloadSpine?.state) return;
    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.reloadSpine.state.setAnimation(0, animations[0], false);
        this.reloadSpine.state.update(0.016);
        this.reloadSpine.skeleton.updateWorldTransform();
      }
      this.reloadSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.reloadSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;

    const animations = this.reloadSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;
    return animations;
  }

  // ===================================================================
  //  VALUE — Respins remaining number (GrekoNew counter font)
  // ===================================================================

  initializeValue() {
    if (!this.reloadSpine || !this.reloadSpine.skeleton) return;

    const slot = this.reloadSpine.skeleton.findSlot("txt_slot_value");
    if (!slot) return;

    this.valueContainer = new PIXI.Container();
    this.valueContainer.name = "pc_reload_value_attached_to_slot";

    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;

    this.valueText = new PIXI.Text(
      "",
      new PIXI.TextStyle(scaleStyle(counterBaseStyle, 340)),
    );
    this.valueText.anchor.set(0.5);

    this.valueContainer.addChild(this.valueText);

    const bone = slot.bone;
    this._valueBoneWidth = bone ? bone.data.length || 400 : 400;

    this.reloadSpine.addSlotObject("txt_slot_value", this.valueContainer);
  }

  updateValueDisplay(respinsLeft) {
    if (!this.valueText) return;

    this.valueText.text = String(respinsLeft);

    if (this.valueContainer) {
      this.valueContainer.scale.set(1);
      if (
        this.valueText.width > this._valueBoneWidth &&
        this._valueBoneWidth > 0
      ) {
        this.valueContainer.scale.set(
          this._valueBoneWidth / this.valueText.width,
        );
      }
    }
  }

  // ===================================================================
  //  RESPINS LABEL — "RESPINS LEFT" text
  // ===================================================================

  initializeRespinsLabel() {
    if (!this.reloadSpine || !this.reloadSpine.skeleton) return;

    const slot = this.reloadSpine.skeleton.findSlot("txt_slot_respins");
    if (!slot) return;

    this.respinsContainer = new PIXI.Container();
    this.respinsContainer.name = "pc_reload_respins_attached_to_slot";

    const style = FONT_STYLES.introTapToStart;
    let fontSize = 72;

    // Get the label text (each word on its own line)
    const labelStr = this.t("popup_respins_recovery_label", { count: this._respinsLeft || 3 })
      .toUpperCase()
      .replace(" ", "\n");

    // Auto-scale fontSize so the widest line fits within 80% of screen width (in local coords)
    const spineScale = this.reloadSpine?.scale?.x || 1;
    const maxPixelWidth = ((this.dimensions?.width || 800) * 0.8) / spineScale;
    const lines = labelStr.split("\n");
    for (const line of lines) {
      const tempStyle = new PIXI.TextStyle(scaleStyle(style, fontSize, { align: "center" }));
      const metrics = PIXI.TextMetrics.measureText(line, tempStyle);
      if (metrics.width > maxPixelWidth && maxPixelWidth > 0) {
        fontSize = Math.floor(fontSize * (maxPixelWidth / metrics.width));
      }
    }

    this.respinsText = new PIXI.Text(
      labelStr,
      new PIXI.TextStyle(scaleStyle(style, fontSize, { align: "center", lineHeight: fontSize })),
    );
    this.respinsText.anchor.set(0.5);

    this.respinsContainer.addChild(this.respinsText);

    // Also check against bone width as fallback
    const bone = slot.bone;
    const boneWidth = bone ? bone.data.length || 400 : 400;
    if (this.respinsText.width > boneWidth && boneWidth > 0) {
      this.respinsContainer.scale.set(boneWidth / this.respinsText.width);
    }

    this.reloadSpine.addSlotObject("txt_slot_respins", this.respinsContainer);
  }

  // ===================================================================
  //  BUTTON — "TAP TO CONTINUE"
  // ===================================================================

  initializeButton() {
    if (!this.reloadSpine || !this.reloadSpine.skeleton) return;

    const tapSlot =
      this.reloadSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) return;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "pc_reload_continue_attached_to_slot";

    const buttonLabel = this.t("tap_to_continue").toUpperCase();

    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      buttonLabel,
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonText);

    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;
    const tapSpineScale = this.reloadSpine?.scale?.x || 1;
    const tapScreenLocal = ((this.dimensions?.width || 800) * 0.8) / tapSpineScale;
    const tapMaxWidth = Math.min(this._tapBoneWidth, tapScreenLocal);
    if (this.buttonText.width > tapMaxWidth && tapMaxWidth > 0) {
      const scale = tapMaxWidth / this.buttonText.width;
      this.buttonContainer.scale.set(scale);
    }

    this.reloadSpine.addSlotObject(
      "txt_slot_tap_to_continue",
      this.buttonContainer,
    );

    // Make the button clickable — triggers fullscreen + dismissal via component handler
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.hitArea = new PIXI.Rectangle(
      -this._tapBoneWidth / 2, -40, this._tapBoneWidth, 80
    );
    this.buttonContainer.on("pointerdown", (evt) => {
      if (evt?.stopPropagation) evt.stopPropagation();
      // Call the handler from the component (fullscreen + dismiss)
      if (this._buttonClickHandler) {
        this._buttonClickHandler(evt);
      }
    });

    this.applyButtonOffset();
  }

  applyButtonOffset() {
    // Spine overwrites container transform each frame — offset text inside container
    if (!this.buttonText) return;
    const config = this.getDeviceMultipliers();
    const yOffset = (config.buttonYOffset || 0) * (this.symbolHeight || 0);
    this.buttonText.y = yOffset;
  }

  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  getSpineAnimationDuration(animationName) {
    if (!this.reloadSpine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.reloadSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );
      if (animation && animation.duration) {
        return Math.round(animation.duration * 1000);
      }
      return 1000;
    } catch {
      return 1000;
    }
  }

  clearAllTimeouts() {
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
    if (this.inIdleTimeout) {
      this.inIdleTimeout.clear();
      this.inIdleTimeout = null;
    }
  }


  // ===================================================================
  //  ANIMATION SEQUENCE
  // ===================================================================

  /**
   * Set the respins remaining for display. Called before startSequence.
   * @param {number} respinsLeft - Number of respins remaining
   */
  setRespinsLeft(respinsLeft) {
    this._respinsLeft = respinsLeft;
    this.updateValueDisplay(this._respinsLeft);
    this.updateRespinsLabelText(this._respinsLeft);
  }

  /**
   * Update the "RESPINS LEFT" / "RESPIN LEFT" label text based on count.
   * Uses ICU plural form so count=1 → singular, count>1 → plural.
   */
  updateRespinsLabelText(count) {
    if (!this.respinsText) return;

    const style = FONT_STYLES.introTapToStart;
    let fontSize = 72;

    const labelStr = this.t("popup_respins_recovery_label", { count })
      .toUpperCase()
      .replace(" ", "\n");

    // Auto-scale fontSize so the widest line fits within 80% of screen width (in local coords)
    const spineScale = this.reloadSpine?.scale?.x || 1;
    const maxPixelWidth = ((this.dimensions?.width || 800) * 0.8) / spineScale;
    const lines = labelStr.split("\n");
    for (const line of lines) {
      const tempStyle = new PIXI.TextStyle(scaleStyle(style, fontSize, { align: "center" }));
      const metrics = PIXI.TextMetrics.measureText(line, tempStyle);
      if (metrics.width > maxPixelWidth && maxPixelWidth > 0) {
        fontSize = Math.floor(fontSize * (maxPixelWidth / metrics.width));
      }
    }

    this.respinsText.text = labelStr;
    this.respinsText.style = new PIXI.TextStyle(scaleStyle(style, fontSize, { align: "center", lineHeight: fontSize }));

    // Re-fit within bone width as fallback
    if (this.respinsContainer) {
      this.respinsContainer.scale.set(1);
      const slot = this.reloadSpine?.skeleton?.findSlot("txt_slot_respins");
      const bone = slot?.bone;
      const boneWidth = bone ? bone.data.length || 400 : 400;
      if (this.respinsText.width > boneWidth && boneWidth > 0) {
        this.respinsContainer.scale.set(boneWidth / this.respinsText.width);
      }
    }
  }

  /**
   * Start the reload popup sequence.
   * Returns a promise that resolves when the user dismisses the popup.
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.reloadSpine?.state) {
        resolve();
        return;
      }

      this._dismissResolve = resolve;
      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.hasHandledCloseClick = false;
      this.overlayRef.current?.fadeInBackground();

      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      this.clearAllTimeouts();

      // Play sounds
      soundManager.play(SOUND_NAME.respins_left_in);

      if (
        this.reloadSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Clear state
      this.reloadSpine.state.clearTracks();
      this.reloadSpine.state.clearListeners();
      this.reloadSpine.state.apply(this.reloadSpine.skeleton);
      this.reloadSpine.skeleton.setToSetupPose();

      // Re-apply offsets after setToSetupPose
      this.applyButtonOffset();

      // SKIP PROTECTION: Block skipping for the first second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play "in" animation while STILL HIDDEN
      this.reloadSpine.state.setAnimation(0, "in", false);

      // Show on NEXT FRAME
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.reloadSpine) {
          this.reloadSpine.alpha = 1;
        }
      });

      const inDuration = this.getSpineAnimationDuration("in");

      this.inIdleTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.reloadSpine.state.setAnimation(0, "idle", true);
          this._startIdleSoundLoop();
        }
      }, inDuration);
    });
  }

  // ===================================================================
  //  USER INTERACTION
  // ===================================================================

  handleUserClick() {
    if (!this.isPlaying || !this.reloadSpine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    this.hasHandledCloseClick = true;
    this.playClosingAnimation();
    return true;
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.reloadSpine?.state) return;

    this.isWaitingForClick = false;

    // Stop sounds
    this._stopIdleSoundLoop();

    this.reloadSpine.state.clearListeners();

    this.reloadSpine.state.setAnimation(0, "out", false);
    this.reloadSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.reloadSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.reloadSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    this._stopIdleSoundLoop();

    this.clearAllTimeouts();

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Close the dialog state
    this.dispatch(setShowPotCollectionReload(false));

    // Re-enable UI — user can now press play button
    this.dispatch(setCriticalAnimationPlaying(false));
    this.dispatch(setUserInterfaceDisabled(false));

    // Fade out the overlay
    this.overlayRef.current?.fadeOutBackground();

    // Resolve the awaiting promise
    if (this._dismissResolve) {
      this._dismissResolve();
      this._dismissResolve = null;
    }
  }

  // ===================================================================
  //  IDLE SOUND LOOP
  // ===================================================================

  _startIdleSoundLoop() {
    this._idleSoundActive = true;
    this._playNextIdleSound();
  }

  _playNextIdleSound() {
    if (!this._idleSoundActive) return;
    soundManager.play(SOUND_NAME.generic_popup_idle);
    const duration = SFX_SPRITE_MAP.generic_popup_idle?.[1] || 2750;
    this._idleSoundTimeout = new TickerTimeout(() => {
      this._playNextIdleSound();
    }, duration);
  }

  _stopIdleSoundLoop() {
    this._idleSoundActive = false;
    if (this._idleSoundTimeout) {
      this._idleSoundTimeout.clear();
      this._idleSoundTimeout = null;
    }
    soundManager.stop(SOUND_NAME.generic_popup_idle);
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    this._stopIdleSoundLoop();
    this.clearAllTimeouts();

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Cleanup value
    if (this.valueContainer) {
      this.valueContainer.destroy({ children: true });
      this.valueContainer = null;
      this.valueText = null;
    }

    // Cleanup respins label
    if (this.respinsContainer) {
      this.respinsContainer.destroy({ children: true });
      this.respinsContainer = null;
      this.respinsText = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    if (this.reloadSpine) {
      this.reloadSpine.state?.clearListeners();
      this.reloadSpine.state?.clearTracks();
      if (this.reloadSpine.state) this.reloadSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.reloadSpine)) {
        this.container.removeChild(this.reloadSpine);
      }
      this.reloadSpine.destroy();
      this.reloadSpine = null;
    }

    this.initialized = false;

    // Clear caches
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
  }
}

export default PotCollectionReloadController;
